'use client'

/*
 * LoginNavButton
 *
 * The Login button in the navbar, plus the MagicLinkLogin modal it controls.
 *
 * WHY THE MODAL USES createPortal:
 * This component lives inside the <nav> element, which is `sticky` with
 * `z-50`. That creates a "stacking context" - a boundary that z-index values
 * can't escape. If we render the modal here as normal JSX, it becomes a child
 * of the navbar and its z-index is relative to the navbar, not the page.
 * The result: the modal gets clipped at the navbar's edges.
 *
 * createPortal tells React: "render this JSX into a different DOM node, at
 * document.body, completely outside the navbar's stacking context." The
 * component's state and props still work normally - only the rendered DOM
 * location changes.
 */

import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { User } from 'lucide-react'
import MagicLinkLogin from './MagicLinkLogin'

export default function LoginNavButton() {
  const [showModal, setShowModal] = useState(false)
  const [mounted, setMounted] = useState(false)

  // createPortal requires `document` which only exists in the browser, not
  // during server rendering. This effect flips `mounted` to true once we're
  // on the client, at which point it's safe to create the portal.
  useEffect(() => {
    setMounted(true)
  }, [])

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        className="flex items-center space-x-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-6 py-2 rounded-lg font-medium transition-all duration-300 shadow-md hover:shadow-lg transform hover:scale-105"
      >
        <User className="w-4 h-4" />
        <span>Login</span>
      </button>

      {/*
        Render the modal at document.body via a portal, so it escapes the
        navbar's sticky stacking context and appears correctly over the
        whole page.
      */}
      {mounted && createPortal(
        <MagicLinkLogin
          isOpen={showModal}
          onClose={() => setShowModal(false)}
        />,
        document.body
      )}
    </>
  )
}